import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Spinner } from "../../../components/Filtering/Reusable/Spinner";
import { useTranslation } from "react-i18next";
import DropDownCountry from "../../../components/buttons/DropDownCountry";
import DropDownDepartment from "../../../components/buttons/DropDownDepartment";
import Dropdown from "../../../components/buttons/Dropdown";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import BreadCrumb from "../../../components/HeaderComponents/BreadCrum";
import { toggleSection, getControlledList } from "../../../utils/constants";
import ArrowIcon from "../../../utils/ArrowIcon";
import { useWorkerApiHelper } from "../../../hooks/useWorkerApiHelper";
import { usePostParticipant } from "../../../hooks/participants/usePostParticipants";
import { showErrorAlert } from "../../../utils/showErrorAlert";
import { translateErrorText } from "../../../utils/errorMessageTranslations";
import MultiLang from "../../../components/MultiLang/MultiLang";
import { availableLanguages } from "../../../i18n";
import {
  addLanguageTab,
  getTextForField,
  removeLanguageTab,
  setTextForField,
} from "../../../utils/formUtils";
import {
  validateEmail,
  validateOpeningHours,
  validatePhone,
} from "../../../utils/utils";
import { buildRegistryParticipantPayload } from "./participantFormData";

function createInitialRegistryFormData(baseLang = "en") {
  return {
    externalIdentifier: "",
    status: "",
    preferredLabel: [{ language: baseLang, value: "" }],
    description: [{ language: baseLang, value: "" }],
    classification: [],
    purpose: [],
    roles: [],
    address: {
      adminUnit1: "",
      adminUnit2: "",
      fullAddress: "",
      postCode: "",
      postName: "",
    },
    contactPoint: {
      email: "",
      telephone: "",
      openingHours: "",
    },
  };
}

function setValueAtPath(prev, path, value) {
  const parts = String(path).split(".");
  const next = { ...prev };
  let cursor = next;
  let sourceCursor = prev;

  for (let i = 0; i < parts.length - 1; i += 1) {
    const part = parts[i];
    const sourceValue = sourceCursor?.[part];
    cursor[part] = Array.isArray(sourceValue)
      ? [...sourceValue]
      : sourceValue && typeof sourceValue === "object"
        ? { ...sourceValue }
        : {};
    cursor = cursor[part];
    sourceCursor = sourceValue;
  }

  cursor[parts[parts.length - 1]] = value;
  return next;
}

function CreateParticipant() {
  const { callApiWithWorker } = useWorkerApiHelper();
  const [isGeneralOpen, setIsGeneralOpen] = useState(true);
  const [isContactOpen, setIsContactOpen] = useState(true);
  const [isTechnicalOpen, setIsTechnicalOpen] = useState(true);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [baseLang] = useState(() => i18n.language);
  const [languageTabs, setLanguageTabs] = useState([]);
  const [activeLang, setActiveLang] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage] = useState("");
  const createMut = usePostParticipant({
    callApiWithWorker,
    t,
    onErrorAlert: showErrorAlert,
  });

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const [controlledVocabularies, setControlledVocabularies] = useState({
    organizationType: [],
    countries: [],
    governmentFunctions: [],
    provinces: [],
    postalCodes: [],
  });

  const [formData, setFormData] = useState(() =>
    createInitialRegistryFormData(baseLang),
  );

  const [isFormValid, setIsFormValid] = useState(false);
  const isTranslationMode = activeLang !== "";

  const getPostNameFromPostalCode = (postalCodeValue, options) => {
    const selectedOption = options.find(
      (option) => option.value === postalCodeValue,
    );

    if (!selectedOption?.label) return "";

    const [, ...labelParts] = String(selectedOption.label).split("-");
    return labelParts.join("-").trim();
  };

  const emailValidation = validateEmail(formData.contactPoint.email, t);
  const emailHasError = !emailValidation.valid;
  const phoneValidation = validatePhone(formData.contactPoint.telephone, t);
  const phoneHasError = !phoneValidation.valid;
  const openingHoursValidation = validateOpeningHours(
    formData.contactPoint.openingHours,
    t,
  );
  const openingHoursHasError = !openingHoursValidation.valid;

  useEffect(() => {
    const requiredFields = ["externalIdentifier"];

    const hasRequiredFields = requiredFields.every((field) => {
      const v = formData[field];
      return typeof v === "string" && v.trim() !== "";
    });

    setIsFormValid(
      hasRequiredFields &&
        validateEmail(formData.contactPoint.email, t).valid &&
        validatePhone(formData.contactPoint.telephone, t).valid &&
        validateOpeningHours(formData.contactPoint.openingHours, t).valid,
    );
  }, [formData]);

  const handleUnifiedChange = (namePathOrEvent, valueOrEvent) => {
    let name, value;

    if (valueOrEvent === undefined) {
      const e = namePathOrEvent;
      name = e.target.name;
      value = e.target.value;
    } else {
      name = namePathOrEvent;
      value = valueOrEvent;
    }

    if (name === "address.postCode") {
      const nextPostName = getPostNameFromPostalCode(value, postalCodeOptions);
      setFormData((prev) =>
        setValueAtPath(
          setValueAtPath(prev, "address.postCode", value),
          "address.postName",
          nextPostName,
        ),
      );
      return;
    }

    setFormData((prev) => setValueAtPath(prev, name, value));
  };

  const purposeOptions = controlledVocabularies.governmentFunctions.map(
    (item) => ({
      label: item.label,
      value: item.concept_iri,
    }),
  );

  const provinceOptions = controlledVocabularies.provinces.map((item) => ({
    label: item.label,
    value: item.concept_iri,
  }));
  const postalCodeOptions = controlledVocabularies.postalCodes.map((item) => ({
    label: item.label,
    value: item.code ?? item.concept_iri,
  }));
  const roleOptions = [
    { label: t("roles.data_provider"), value: "data_provider" },
    { label: t("roles.data_consumer"), value: "data_consumer" },
  ];

  const languageOptions = availableLanguages;
  const translatableFields = ["preferredLabel", "description"];

  const getLangLabel = (code) =>
    languageOptions.find((option) => option.code === code)?.label || code;

  const handleAddLanguageTab = (code) => {
    addLanguageTab(
      code,
      baseLang,
      languageTabs,
      translatableFields,
      formData,
      setFormData,
      setLanguageTabs,
      setActiveLang,
    );
  };

  const handleRemoveLang = (langCode) => {
    removeLanguageTab(
      langCode,
      activeLang,
      translatableFields,
      setFormData,
      setLanguageTabs,
      setActiveLang,
    );
  };

  const renderTranslatableInput = (
    fieldName,
    placeholder,
    isTextarea,
    mirroredFields = [],
  ) => {
    const value = getTextForField(formData, fieldName, activeLang, baseLang);
    const lang = activeLang === "" ? baseLang : activeLang;
    const handleChange = (nextValue) =>
      setFormData((prev) => {
        let next = setTextForField(prev, fieldName, lang, nextValue);
        mirroredFields.forEach((mirrorField) => {
          next = setTextForField(next, mirrorField, lang, nextValue);
        });
        return next;
      });

    if (isTextarea) {
      return (
        <textarea
          className="inputoffer fontNoto"
          name={fieldName}
          placeholder={placeholder}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
        />
      );
    }

    return (
      <input
        className="inputoffer"
        type="text"
        placeholder={placeholder}
        name={fieldName}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
      />
    );
  };

  useEffect(() => {
    async function fetchVocabularies() {
      const [
        organizationType,
        countries,
        governmentFunctions,
        provinces,
        postalCodes,
      ] = await Promise.all([
        getControlledList(callApiWithWorker, "organizationtypes-eu", {
          lang: i18n.language,
          connector: "springConnector",
        }),
        getControlledList(callApiWithWorker, "countries", {
          lang: i18n.language,
          connector: "springConnector",
        }),
        getControlledList(callApiWithWorker, "government-functions", {
          lang: i18n.language,
          connector: "springConnector",
        }),
        getControlledList(callApiWithWorker, "provincias", {
          lang: i18n.language,
          connector: "springConnector",
        }),
        getControlledList(callApiWithWorker, "codigos-postales", {
          lang: i18n.language,
          connector: "springConnector",
          size: 16000,
        }),
      ]);

      setControlledVocabularies({
        organizationType,
        countries,
        governmentFunctions,
        provinces,
        postalCodes,
      });
    }
    fetchVocabularies();
  }, [i18n.language]);

  const toggleGeneral = () =>
    toggleSection(setIsGeneralOpen, ".createdivgeneral");
  const toggleContact = () =>
    toggleSection(setIsContactOpen, ".createdivdetails");
  const toggleTechnical = () =>
    toggleSection(setIsTechnicalOpen, ".createdivtechnical");

  const createResource = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = buildRegistryParticipantPayload(formData, baseLang, {
      postalCodeOptions: controlledVocabularies.postalCodes,
      provinceOptions: controlledVocabularies.provinces,
    });

    try {
      const connectorsCount = Array.isArray(formData.roles)
        ? Number(formData.roles.length)
        : 0;

      await createMut.mutateAsync({ payload, connectorsCount });

      setLoading(false);
      navigate(-1);
    } catch (error) {
      console.error("Error en la petición:", error);
      setLoading(false);
      const errorStatus =
        error.response?.status ||
        error.response?.data?.statusCode ||
        error.response?.data?.status;
      const errorMessage =
        error.response?.data?.message || t("participant.errorMessage");

      Swal.fire({
        title: error.response?.data?.status || t("participant.errorTitle"),
        text: translateErrorText(errorMessage, errorStatus),
        icon: "error",
      });
    }
  };

  return (
    <section className="grisback pb4">
      <div className="whiteback W100">
        <div>
          <BreadCrumb />
        </div>
      </div>
      <div className="containerflex">
        <div className="whiteback W100 padding2rem">
          <div className="w90">
            <h2 className="tittlecatalog">{t("participant.addParticipant")}</h2>
            <MultiLang
              t={t}
              baseLang={baseLang}
              languageTabs={languageTabs}
              languageOptions={languageOptions}
              activeLang={activeLang}
              addLanguageTab={handleAddLanguageTab}
              removeLanguageTab={handleRemoveLang}
              setActiveLang={setActiveLang}
              getLangLabel={getLangLabel}
            />
          </div>
        </div>
        <div className="W100 containerflex ptb2">
          <div className="w95 bgwhite">
            <div className="creatediv">
              <div
                className="createdivgeneral w100"
                onClick={toggleGeneral}
                style={{ cursor: "pointer" }}
              >
                <h4 className="ptb3lr4">{t("participant.idData")}</h4>
                <ArrowIcon open={isGeneralOpen} />
              </div>
              {isGeneralOpen && (
                <div
                  className="input-fields"
                  style={{ transition: "all 0.3s ease" }}
                >
                  <div className="resource-info">
                    <div className="form-rows">
                      <div className="form-row">
                        <div className="form-group">
                          <p className="ptitleinput">{t("participant.name")}</p>
                          {renderTranslatableInput(
                            "preferredLabel",
                            t("participant.name_placeholder"),
                            false,
                          )}
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("dataModelDetail.detail.country")}
                          </p>
                          <DropDownCountry
                            value={
                              formData.address.adminUnit1
                                ? [formData.address.adminUnit1]
                                : []
                            }
                            onChange={(newCountry) =>
                              handleUnifiedChange(
                                "address.adminUnit1",
                                Array.isArray(newCountry)
                                  ? (newCountry[0] ?? "")
                                  : newCountry,
                              )
                            }
                            options={controlledVocabularies.countries.map(
                              (item) => item.label,
                            )}
                            optionsValue={controlledVocabularies.countries.map(
                              (item) => item.concept_iri,
                            )}
                            placeholderText={t("dropdown.countrytwo")}
                            disabled={isTranslationMode}
                          />
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.nif")}
                            <span className="required">*</span>
                          </p>
                          <input
                            className="inputoffer"
                            type="text"
                            placeholder={t("participant.nif_placeholder")}
                            name="externalIdentifier"
                            value={formData.externalIdentifier}
                            onChange={handleUnifiedChange}
                            disabled={isTranslationMode}
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t(
                              "createDataOfferingsDetail.policiesRules.department",
                            )}
                          </p>
                          <DropDownDepartment
                            value={formData.classification[0] ?? ""}
                            onChange={(departmentValue) =>
                              handleUnifiedChange(
                                "classification",
                                departmentValue ? [departmentValue] : [],
                              )
                            }
                            options={controlledVocabularies.organizationType.map(
                              (item) => item.label,
                            )}
                            optionsValue={controlledVocabularies.organizationType.map(
                              (item) => item.concept_iri,
                            )}
                            disabled={isTranslationMode}
                          />
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.purpose")}
                          </p>
                          <Dropdown
                            variant="autocomplete"
                            multiple
                            value={
                              Array.isArray(formData.purpose)
                                ? formData.purpose
                                : []
                            }
                            onChange={(values) =>
                              handleUnifiedChange("purpose", values)
                            }
                            options={purposeOptions}
                            placeholderKey="participant.purpose_placeholder"
                            disabled={isTranslationMode}
                          />
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.address")}
                          </p>
                          <input
                            className="inputoffer"
                            type="text"
                            placeholder={t("participant.address_placeholder")}
                            name="address.fullAddress"
                            value={formData.address.fullAddress}
                            onChange={handleUnifiedChange}
                            disabled={isTranslationMode}
                          />
                        </div>
                      </div>
                      <div className="form-row form-row-two-cols">
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.description")}
                          </p>
                          {renderTranslatableInput(
                            "description",
                            t("participant.description_placeholder"),
                            true,
                          )}
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.email")}
                          </p>
                          <input
                            className="inputoffer"
                            type="email"
                            placeholder={t("participant.email_placeholder")}
                            name="contactPoint.email"
                            value={formData.contactPoint.email}
                            onChange={handleUnifiedChange}
                            disabled={isTranslationMode}
                          />
                          {emailHasError && (
                            <p className="error_text">
                              {emailValidation.error}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="creatediv">
              <div
                className="createdivdetails w100"
                onClick={toggleContact}
                style={{ cursor: "pointer" }}
              >
                <h4 className="ptb3lr4">{t("participant.contactData")}</h4>
                <ArrowIcon open={isContactOpen} />
              </div>
              {isContactOpen && (
                <div
                  className="input-fields"
                  style={{ transition: "all 0.3s ease" }}
                >
                  <div className="resource-info">
                    <div className="form-rows">
                      <div className="form-row-tight participant-contact-row">
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.adminUnit2")}
                          </p>
                          <Dropdown
                            variant="autocomplete"
                            value={formData.address.adminUnit2}
                            onChange={(value) =>
                              handleUnifiedChange("address.adminUnit2", value)
                            }
                            options={provinceOptions}
                            placeholderKey="participant.adminUnit2_placeholder"
                            disabled={isTranslationMode}
                          />
                        </div>

                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.postal")}
                          </p>
                          <Dropdown
                            variant="autocomplete"
                            value={formData.address.postCode}
                            onChange={(value) =>
                              handleUnifiedChange("address.postCode", value)
                            }
                            options={postalCodeOptions}
                            placeholderKey="participant.postal_placeholder"
                            disabled={isTranslationMode}
                          />
                        </div>

                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.postalName")}
                          </p>
                          <input
                            className="inputoffer"
                            type="text"
                            name="address.postName"
                            placeholder={t(
                              "participant.postalName_placeholder",
                            )}
                            value={formData.address.postName}
                            onChange={handleUnifiedChange}
                            disabled={isTranslationMode}
                          />
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.phone")}
                          </p>
                          <input
                            className="inputoffer"
                            type="text"
                            name="contactPoint.telephone"
                            placeholder={t("participant.phone_placeholder")}
                            value={formData.contactPoint.telephone}
                            onChange={handleUnifiedChange}
                            disabled={isTranslationMode}
                          />
                          {phoneHasError && (
                            <p className="error_text">
                              {phoneValidation.error}
                            </p>
                          )}
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("participant.openingHours")}
                          </p>
                          <input
                            className="inputoffer"
                            type="text"
                            name="contactPoint.openingHours"
                            placeholder={t(
                              "participant.openingHours_placeholder",
                            )}
                            value={formData.contactPoint.openingHours}
                            onChange={handleUnifiedChange}
                            disabled={isTranslationMode}
                          />
                          {openingHoursHasError && (
                            <p className="error_text">
                              {openingHoursValidation.error}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="creatediv">
              <div
                className="createdivdetails createdivtechnical w100"
                onClick={toggleTechnical}
                style={{ cursor: "pointer" }}
              >
                <h4 className="ptb3lr4">{t("participant.technicalData")}</h4>
                <ArrowIcon open={isTechnicalOpen} />
              </div>
              {isTechnicalOpen && (
                <div
                  className="input-fields"
                  style={{ transition: "all 0.3s ease" }}
                >
                  <div className="resource-info">
                    <div className="form-rows">
                      <div className="form-row">
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("usermanagement.role")}
                          </p>
                          <Dropdown
                            variant="autocomplete"
                            multiple
                            value={
                              Array.isArray(formData.roles)
                                ? formData.roles
                                : []
                            }
                            onChange={(values) =>
                              handleUnifiedChange("roles", values)
                            }
                            options={roleOptions}
                            placeholderKey="participant.roles_placeholder"
                            disabled={isTranslationMode}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="whiteback W100 p2">
        <div className="w90 flexdivbotones">
          <button
            className="boton botoncancel"
            onClick={() => {
              navigate(-1);
            }}
          >
            {t("createDataOfferingsDetail.cancel")}
          </button>
          <button
            type="submit"
            className={`boton botoncapply ${
              !isFormValid ? "disabled-button" : ""
            }`}
            disabled={!isFormValid}
            onClick={createResource}
          >
            {t("createDataModelDetail.create")}
          </button>
        </div>
      </div>
      <div>
        {loading && (
          <div className="loading">
            <Spinner size="large" color="var(--main-color)" />
          </div>
        )}
      </div>
      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>
          {t("createDataModelDetail.messages.information")}
        </DialogTitle>
        <DialogContent>{modalMessage}</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>OK</Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}

export default CreateParticipant;
